import { useMemo, useRef, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"

import moment from "moment"

import { Pagination } from "components"
import Table from "components/table"
import Input from "components/forms/input"
import Label from "components/forms/label"
import SelectDescription from "components/forms/select-description"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { useAuth } from "contexts/auth-context"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

import useStockAdjustmentRepositories from "repositories/stock-adjustment"

const STATUS_OPTIONS = [
  { id: "ALL", name: "Semua Status" },
  { id: "DRAFT", name: "Draft" },
  { id: "POSTED", name: "Posted" },
  { id: "CANCELLED", name: "Cancelled" },
]

export default function List() {
  const { can, isSuper, currentBranch, canFilterAllOutlets } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchInput = useRef(null)
  const [searchText, setSearchText] = useState(searchParams.get("search") || "")

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
  const rawOutletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const outletId = isBranchLocked ? String(currentBranch.id) : rawOutletId
  // Grup → CSV id (mis. "1,15"); outlet individual → id tunggal.
  const { outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const status = searchParams.get("status") || "ALL"
  const dateFrom = searchParams.get("date_from") || ""
  const dateTo = searchParams.get("date_to") || ""
  const page = searchParams.get("page") || "1"

  const { data, isLoading, mutate } = useStockAdjustmentRepositories({
    search: searchParams.get("search") || undefined,
    outlet_id: resolvedOutletCsv || undefined,
    status: status === "ALL" ? undefined : status,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page,
  })

  const rows = useMemo(() => data?.data ?? [], [data])
  const canCreateAdjustment = can("stock-adjustment.create") || can("stock_adjustment.create")

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }

    Object.keys(merged).forEach((key) => {
      if (merged[key] === null || merged[key] === undefined || merged[key] === "") {
        delete merged[key]
      }
    })

    setSearchParams(merged)
  }

  const submitSearch = () => {
    mergeParams({ search: searchText || null, page: 1 })
  }

  const cancelDraft = async (row) => {
    const confirmed = window.confirm(`Batalkan draft stock adjustment ${row.adjustment_no || ""}?`)
    if (!confirmed) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${row.id}/cancel`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
        }
      )

      const json = await response.json()
      if (!response.ok || Number(json?.success) !== 1) {
        throw new Error(json?.message || "Gagal membatalkan draft stock adjustment")
      }

      toast.success(json?.message || "Draft stock adjustment berhasil dibatalkan")
      await mutate()
    } catch (error) {
      toast.error(error?.message || "Gagal membatalkan draft stock adjustment")
    }
  }

  const headers = [
    { isAction: false, content: "No. Adjustment" },
    { isAction: false, content: "Tanggal" },
    { isAction: false, content: "Outlet" },
    { isAction: false, content: "Status" },
    // 240726 (permintaan user): mode input & arah koreksi.
    { isAction: false, content: "Mode" },
    { isAction: false, content: "Arah" },
    { isAction: false, content: "Item" },
    { isAction: false, content: "Dibuat Oleh" },
    { isAction: false, content: "Catatan" },
    { isAction: true, content: "Aksi" },
  ]

  const columns = [
    { type: "default", bold: true, content: (v) => v.adjustment_no || "-" },
    { type: "default", content: (v) => v.adjustment_date ? moment(v.adjustment_date).format("YYYY-MM-DD HH:mm") : "-" },
    { type: "default", content: (v) => v.outlet || "-" },
    {
      type: "default",
      content: (v) => (
        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${
          v.status === "DRAFT"
            ? "bg-amber-50 text-amber-700 border-amber-200"
            : v.status === "POSTED"
            ? "bg-green-50 text-green-700 border-green-200"
            : "bg-neutral-50 text-neutral-700 border-neutral-200"
        }`}>
          {v.status}
        </span>
      ),
    },
    // 240726 (permintaan user): mode input (Stok Fisik / Masuk-Keluar) & arah koreksi.
    {
      type: "default",
      content: (v) => (
        <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-md ${
          v.input_mode === "DELTA" ? "bg-blue-50 text-blue-700" : "bg-neutral-100 text-neutral-700"
        }`}>
          {v.input_mode_label || "-"}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className={`inline-flex px-2 py-0.5 text-[11px] font-medium rounded-md ${
          v.direction === "IN" ? "bg-green-50 text-green-700"
            : v.direction === "OUT" ? "bg-red-50 text-red-700"
            : v.direction === "MIXED" ? "bg-amber-50 text-amber-700"
            : "bg-neutral-100 text-neutral-500"
        }`}>
          {v.direction_label || "-"}
        </span>
      ),
    },
    { type: "default", content: (v) => v.total_items || 0 },
    { type: "default", content: (v) => v.created_by || "-" },
    { type: "default", content: (v) => v.notes || "-" },
    {
      type: "action",
      content: (v) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              window.open(
                `${process.env.REACT_APP_INVENTORY_API_URL}/stock-adjustments/${v.id}/print`,
                "_blank"
              )
            }}
            className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg"
          >
            Cetak PDF
          </button>
          {v.status === "DRAFT" ? (
            <>
              <Link
                to={`/stock-adjustment/${v.id}/edit`}
                className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg"
              >
                Edit Draft
              </Link>
              <button
                type="button"
                onClick={() => cancelDraft(v)}
                className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg"
              >
                Batal
              </button>
            </>
          ) : (
            <Link
              to={`/stock-adjustment/${v.id}/edit`}
              className="px-3 py-1.5 text-xs font-medium text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-lg"
            >
              Lihat
            </Link>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Koreksi Stok</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Daftar stock adjustment dengan filter, status draft, dan tombol untuk membuat adjustment baru.
            </p>
          </div>

          {canCreateAdjustment && (
            <Link
              to="/stock-adjustment/create"
              className="inline-flex items-center px-4 py-3 text-sm font-medium text-white bg-brand-900 rounded-xl"
            >
              Tambah Koreksi Stok
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 mt-5 md:grid-cols-5">
          <div>
            <Label value="Outlet" />
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeParams({ outlet_id: v, page: 1 })}
              disabled={isBranchLocked}
              className="w-full mt-1 p-2 text-sm transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
            />
          </div>
          <div>
            <Label value="Status" />
            <SelectDescription
              selection={STATUS_OPTIONS}
              value={status}
              onChange={(value) => mergeParams({ status: value || "ALL", page: 1 })}
              keyValue={(v) => v.id}
              title={(v) => v.name}
              description={() => ""}
              placeholder="Pilih Status..."
            />
          </div>
          <div>
            <Label value="Tanggal Dari" />
            <Input type="date" value={dateFrom} onChange={(e) => mergeParams({ date_from: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Tanggal Sampai" />
            <Input type="date" value={dateTo} onChange={(e) => mergeParams({ date_to: e.target.value, page: 1 })} />
          </div>
          <div>
            <Label value="Cari" />
            <Input
              ref={searchInput}
              type="text"
              value={searchText}
              placeholder="No. adjustment / catatan..."
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-2xl border-neutral-200">
        <Table
          isLoading={isLoading}
          data={rows}
          headers={headers}
          columns={columns}
          key={(v) => v.id}
          isEmpty={!rows.length}
        />
        <Pagination links={data?.links} from={data?.from} to={data?.to} total={data?.total} />
      </div>
    </div>
  )
}
